import React from "react";
import {
  RocketOutlined,
  UserOutlined,
  TeamOutlined,
  BankOutlined,
  CheckCircleOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { BillingCycle, Plan } from "../subscription.types";

const PLAN_ICON: Record<Plan["id"], React.ReactNode> = {
  free: <RocketOutlined />,
  individual: <UserOutlined />,
  professional: <TeamOutlined />,
  enterprise: <BankOutlined />,
};

interface PlanCardProps {
  plan: Plan;
  cycle: BillingCycle;
  isCurrent: boolean;
  cancelled: boolean;
  /** When cancelled: the date access ends (the org lands on Free after it). */
  accessUntil?: string | null;
  /** True while a reactivate request is in flight (any plan) — disables the button. */
  reactivating?: boolean;
  onChoose: (plan: Plan) => void;
  onContact: () => void;
  onReactivate: (plan: Plan) => void;
  onDetails: (plan: Plan) => void;
}

const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  cycle,
  isCurrent,
  cancelled,
  accessUntil,
  reactivating,
  onChoose,
  onContact,
  onReactivate,
  onDetails,
}) => {
  const { t } = useTranslation("subscription");
  const showSave = cycle === "yearly" && !!plan.yearlySaveLabel;
  const highlight = isCurrent && !cancelled;

  const renderPrice = () => {
    if (plan.isFree) {
      return (
        <div className="sub-plan-price">
          <span className="amount">{t("planCard.free")}</span>
          <span className="per">{t("planCard.noCardNeeded")}</span>
        </div>
      );
    }
    if (plan.customPricing) {
      return (
        <div className="sub-plan-price">
          <span className="custom">{t("planCard.customPricing")}</span>
          <span className="custom-note">
            {t("planCard.customNote.annualContracts")}
            <br />
            {t("planCard.customNote.invoicePoBilling")}
          </span>
        </div>
      );
    }
    const amount =
      cycle === "yearly" ? plan.yearlyMonthlyPrice : plan.monthlyPrice;
    const extraUserPrice =
      cycle === "yearly" ? plan.additionalUserPriceYearly : plan.additionalUserPrice;

    if (plan.includedWorkingUsers != null) {
      const extraSeats =
        plan.workingUserLimit != null
          ? plan.workingUserLimit - plan.includedWorkingUsers
          : null;
      return (
        <>
          <div className="sub-plan-price">
            <span className="amount">${amount}</span>
            <span className="per">{t("planCard.perMonth")}</span>
          </div>
          {cycle === "yearly" && (
            <div className="sub-plan-year">
              {t("planCard.billedPerYear", { amount: plan.yearlyTotal })}
            </div>
          )}
          <div className="sub-plan-seat-note">
            {t("planCard.includesWorkingUser", {
              count: plan.includedWorkingUsers,
            })}
            {extraUserPrice != null && extraSeats != null && extraSeats > 0 && (
              <>
                <br />
                {t("planCard.addUpTo", {
                  count: extraSeats,
                  price: extraUserPrice,
                })}
              </>
            )}
          </div>
        </>
      );
    }

    return (
      <>
        <div className="sub-plan-price">
          <span className="amount">${amount}</span>
          <span className="per">{t("planCard.perUserPerMonth")}</span>
        </div>
        {cycle === "yearly" && (
          <div className="sub-plan-year">
            {t("planCard.billedPerUserPerYear", { amount: plan.yearlyTotal })}
          </div>
        )}
      </>
    );
  };

  const renderCta = () => {
    if (plan.customPricing) {
      return (
        <button className="sub-plan-cta" onClick={onContact}>
          {cancelled ? t("planCard.contactUs") : t("planCard.chooseThisPlan")}
        </button>
      );
    }
    if (isCurrent && !cancelled) {
      return (
        <button className="sub-plan-cta current" disabled>
          <CheckOutlined /> {t("planCard.currentPlan")}
        </button>
      );
    }
    if (cancelled && plan.isFree) {
      // The downgrade is already scheduled: a cancelled subscription lands on
      // Free once access ends — there's nothing to click.
      return (
        <button className="sub-plan-cta current" disabled>
          <CheckOutlined />{" "}
          {accessUntil
            ? t("planCard.freeAfter", { date: accessUntil })
            : t("planCard.downgradeScheduled")}
        </button>
      );
    }
    if (cancelled && !plan.isFree) {
      return (
        <button
          className="sub-plan-cta"
          disabled={reactivating}
          onClick={() => onReactivate(plan)}
        >
          {reactivating
            ? t("planCard.reactivating")
            : t("planCard.reactivateThisPlan")}
        </button>
      );
    }
    return (
      <button className="sub-plan-cta" onClick={() => onChoose(plan)}>
        {plan.isFree ? t("planCard.switchToFree") : t("planCard.chooseThisPlan")}
      </button>
    );
  };

  return (
    <div className={`sub-plan${highlight ? " current" : ""}`}>
      {plan.mostPopular && (
        <span className="sub-plan-popular">{t("common.mostPopular")}</span>
      )}

      <div className="sub-plan-top">
        <span className="sub-plan-name">
          {PLAN_ICON[plan.id]}
          {plan.name}
        </span>
        {showSave && <span className="sub-plan-save">{plan.yearlySaveLabel}</span>}
      </div>

      <p className="sub-plan-tagline">{plan.tagline}</p>

      {renderPrice()}

      <hr className="sub-plan-divider" />
      <div className="sub-plan-includes">{t("planCard.planIncludes")}</div>
      <ul className="sub-plan-features">
        {plan.features.map((f) => (
          <li key={f}>
            <CheckCircleOutlined className="tick" />
            {f}
          </li>
        ))}
      </ul>

      <div className="sub-plan-foot">
        <button className="sub-plan-details-link" onClick={() => onDetails(plan)}>
          {t("planCard.seeAllPlanDetails")}
        </button>
        {renderCta()}
      </div>
    </div>
  );
};

export default PlanCard;
